import type { SedrRequirement } from '../../api/sedr';
import { t } from '../../i18n';
import { formatSedrDate, displaySedrCategory, tSedrStatus } from './sedrUi';

export const SEDR_EXPORT_COLUMNS = [
  'requirementCode',
  'title',
  'description',
  'category',
  'status',
  'owner',
  'testMethodFrequency',
  'lastTestDate',
  'linkedEvidence',
  'artifacts',
  'approvedAt',
  'approvedBy',
] as const;

export type SedrExportColumn = (typeof SEDR_EXPORT_COLUMNS)[number];

const CSV_HEADERS: Record<SedrExportColumn, string> = {
  requirementCode: 'RequirementCode',
  title: 'Title',
  description: 'Description',
  category: 'Category',
  status: 'Status',
  owner: 'Owner',
  testMethodFrequency: 'TestMethodFrequency',
  lastTestDate: 'LastTestDate',
  linkedEvidence: 'LinkedEvidence',
  artifacts: 'Artifacts',
  approvedAt: 'ApprovedAt',
  approvedBy: 'ApprovedBy',
};

function csvEscape(value: string): string {
  if (/[",\n\r]/.test(value)) {
    return `"${value.replace(/"/g, '""')}"`;
  }
  return value;
}

function cellValue(requirement: SedrRequirement, column: SedrExportColumn): string {
  switch (column) {
    case 'requirementCode':
      return requirement.requirementCode;
    case 'title':
      return requirement.title;
    case 'description':
      return requirement.description ?? '';
    case 'category':
      return displaySedrCategory(requirement.category, requirement.categoryName);
    case 'status':
      return tSedrStatus(requirement.status);
    case 'owner':
      return requirement.ownerName ?? '';
    case 'testMethodFrequency':
      return requirement.testMethodFrequency ?? '';
    case 'lastTestDate':
      return formatSedrDate(requirement.lastTestDate);
    case 'linkedEvidence':
      return requirement.linkedFileName ?? '';
    case 'artifacts':
      return requirement.artifacts.map((a) => a.name).join('; ');
    case 'approvedAt':
      return formatSedrDate(requirement.approvedAt);
    case 'approvedBy':
      return requirement.approvedByDisplay ?? '';
  }
}

function reportCellValue(requirement: SedrRequirement, column: SedrExportColumn): string {
  if (column === 'category') {
    return displaySedrCategory(requirement.category, requirement.categoryName);
  }
  const value = cellValue(requirement, column);
  if (column === 'owner' || column === 'linkedEvidence' || column === 'artifacts') {
    return value || '—';
  }
  return value || '—';
}

export function exportSedrRequirementsCsv(
  requirements: SedrRequirement[],
  options?: { filename?: string; columns?: SedrExportColumn[] },
): void {
  const columns = options?.columns?.length ? options.columns : [...SEDR_EXPORT_COLUMNS];
  const filename = options?.filename ?? 'sedr-compliance.csv';
  const header = columns.map((column) => CSV_HEADERS[column]);
  const rows = requirements.map((requirement) =>
    columns.map((column) => csvEscape(cellValue(requirement, column))).join(','));

  const csv = `\uFEFF${header.join(',')}\n${rows.join('\n')}`;
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

export function openSedrComplianceReport(options: {
  title: string;
  clientOrganization: string;
  requirements: SedrRequirement[];
  columns?: SedrExportColumn[];
}): void {
  const columns = options.columns?.length ? options.columns : [
    'requirementCode',
    'title',
    'status',
    'owner',
    'linkedEvidence',
    'artifacts',
  ] satisfies SedrExportColumn[];

  const headerCells = columns
    .map((column) => `<th>${escape(t(`sedr.export.col.${column}`))}</th>`)
    .join('');
  const rows = options.requirements.map((requirement) => `
    <tr>
      ${columns.map((column) => `<td>${escape(reportCellValue(requirement, column))}</td>`).join('')}
    </tr>
  `).join('');

  const html = `<!doctype html>
<html><head><meta charset="utf-8"><title>${escape(options.title)}</title>
<style>
  body { font-family: system-ui, sans-serif; margin: 2rem; color: #111; }
  h1 { margin-bottom: 0.25rem; }
  .meta { color: #555; margin-bottom: 1.5rem; }
  table { border-collapse: collapse; width: 100%; font-size: 12px; }
  th, td { border: 1px solid #ccc; padding: 6px 8px; text-align: left; vertical-align: top; }
  th { background: #f4f4f4; }
  @media print { button { display: none; } }
</style></head><body>
  <button type="button" id="sedr-report-print">${escape(t('sedr.report.print'))}</button>
  <h1>${escape(options.title)}</h1>
  <p class="meta">${escape(options.clientOrganization)} · ${escape(t('sedr.report.generated', { date: new Date().toLocaleString(), interpolation: { escapeValue: false } }))}</p>
  <table>
    <thead>
      <tr>${headerCells}</tr>
    </thead>
    <tbody>${rows}</tbody>
  </table>
</body></html>`;

  const win = window.open('', '_blank');
  if (!win) return;
  win.document.write(html);
  win.document.close();
  win.document.getElementById('sedr-report-print')
    ?.addEventListener('click', () => win.print());
}

function escape(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}
